
import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

interface AIBlogGenerationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prompt: string;
  onPromptChange: (prompt: string) => void;
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  isGenerating: boolean;
  onGenerate: () => void;
}

const AIBlogGenerationDialog: React.FC<AIBlogGenerationDialogProps> = ({
  open,
  onOpenChange,
  prompt,
  onPromptChange,
  date,
  onDateChange,
  isGenerating,
  onGenerate,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate Blog with AI</DialogTitle>
          <DialogDescription>
            Provide a topic or prompt for the AI to generate a draft blog post.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Label htmlFor="blog-prompt">Topic or prompt</Label>
          <Textarea 
            id="blog-prompt" 
            placeholder="e.g., Write about the top 5 strategies for SaaS resellers to increase customer retention"
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            rows={4}
          />
          
          <div className="flex items-center space-x-2">
            <Label htmlFor="blog-category">Category</Label>
            <Select defaultValue="business">
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="business">Business</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
                <SelectItem value="trends">Trends</SelectItem>
                <SelectItem value="customer">Customer Success</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center space-x-2">
            <Label htmlFor="publish-date">Publication Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={onDateChange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <DialogFooter>
          <Button 
            variant="secondary" 
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button 
            onClick={onGenerate}
            disabled={isGenerating || !prompt.trim()}
          >
            {isGenerating ? "Generating..." : "Generate Blog"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AIBlogGenerationDialog;
